import { Component, OnInit, inject, signal, computed, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { PreventionCampaignsService, CampaignListRow } from '../../core/services/prevention-campaigns.service';
import { ToastService } from '../../core/services/toast.service';
import { ConfirmModalComponent } from '../../component/confirm-modal/confirm-modal.component';

/**
 * Écran "Comm prévention" (Lot V1, 2026-09-08) : créer/diffuser/planifier les campagnes de
 * prévention -- distinct de "Prévention > Campagne de prévention" (`/pilotage-prevention/demande`,
 * mesurer/piloter, écran analytics existant non touché par ce lot).
 */
@Component({
  selector: 'app-comm-prevention-list',
  standalone: true,
  imports: [CommonModule, TranslatePipe, ConfirmModalComponent],
  templateUrl: './comm-prevention-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./comm-prevention-list.component.scss'],
})
export class CommPreventionListComponent implements OnInit {
  private api = inject(PreventionCampaignsService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private toastService = inject(ToastService);
  private translate = inject(TranslateService);

  campaigns = signal<CampaignListRow[]>([]);
  loading = signal(true);
  loadError = signal(false);

  suspendTarget = signal<CampaignListRow | null>(null);
  suspending = signal(false);

  /** `?subject=` (posé par le chevron de "Campagne de prévention" quand plusieurs communications
   *  -- ou aucune -- partagent le même sujet que le topic cliqué, voir
   *  prevention-demand.component.ts#openCampaignForTopic) : filtre la liste plutôt qu'une
   *  correspondance devinée à l'aveugle. Dérivé de `route.queryParamMap` (jamais un snapshot figé)
   *  pour rester à jour si l'utilisateur navigue en arrière/avant entre deux `?subject=` différents
   *  sans quitter cette route (Angular ne réinstancie pas le composant, ngOnInit ne se redéclenche pas). */
  private readonly queryParamMap = toSignal(this.route.queryParamMap, { initialValue: this.route.snapshot.queryParamMap });
  readonly subjectFilter = computed(() => this.queryParamMap().get('subject'));

  readonly filteredCampaigns = computed(() => {
    const filter = this.subjectFilter()?.trim().toLowerCase();
    if (!filter) return this.campaigns();
    return this.campaigns().filter(c => c.subject.trim().toLowerCase() === filter);
  });

  ngOnInit(): void {
    this.load();
  }

  clearSubjectFilter(): void {
    this.router.navigate(['/comm-prevention']);
  }

  private load(): void {
    this.loading.set(true);
    this.loadError.set(false);
    this.api.listCampaigns().subscribe({
      next: (rows) => {
        this.campaigns.set(rows);
        this.loading.set(false);
      },
      error: () => {
        this.loadError.set(true);
        this.loading.set(false);
      }
    });
  }

  goCreate(): void {
    this.router.navigate(['/comm-prevention/nouvelle']);
  }

  goView(campaign: CampaignListRow): void {
    this.router.navigate(['/comm-prevention', campaign.campaignId]);
  }

  formatDates(row: CampaignListRow): string {
    const starts = row.startsAt ? new Date(row.startsAt).toLocaleDateString() : null;
    const ends = row.endsAt ? new Date(row.endsAt).toLocaleDateString() : null;
    if (!starts && !ends) return this.translate.instant('commPrevention.list.noDates');
    if (starts && ends) return `${starts} → ${ends}`;
    if (starts) return `${this.translate.instant('commPrevention.list.fromDate')} ${starts}`;
    return `${this.translate.instant('commPrevention.list.untilDate')} ${ends}`;
  }

  openSuspendConfirm(row: CampaignListRow, event: Event): void {
    event.stopPropagation();
    this.suspendTarget.set(row);
  }

  closeSuspendConfirm(): void {
    this.suspendTarget.set(null);
  }

  confirmSuspend(): void {
    const target = this.suspendTarget();
    if (!target) return;
    this.suspending.set(true);
    this.api.suspendCampaign(target.campaignId).subscribe({
      next: () => {
        this.suspending.set(false);
        this.suspendTarget.set(null);
        this.toastService.show(
          this.translate.instant('commPrevention.toast.suspendedTitle'), 'success', 3000,
          this.translate.instant('commPrevention.toast.suspendedMessage'));
        this.load();
      },
      error: () => {
        this.suspending.set(false);
        this.toastService.show(
          this.translate.instant('commPrevention.toast.errorTitle'), 'error', 4000,
          this.translate.instant('commPrevention.toast.suspendError'));
      }
    });
  }
}
